/** Year Bidmio was founded — matches the About page FAQ. */
export const BIDMIO_FOUNDING_YEAR = 2020;

export interface CompanyYearVars {
  /** Footer range, e.g. "2020–2026". */
  year: string;
  /** Full years since founding, e.g. "6". */
  years: string;
  /** Calendar year, e.g. "2026". */
  currentYear: string;
  /** Calendar year before, e.g. "2025" — for copy like "{{previousYear}} vs. {{currentYear}}". */
  previousYear: string;
  /** Calendar year after, e.g. "2027" — for forward-looking copy that must not go stale. */
  nextYear: string;
}

/**
 * Copyright year range for the footer: "2020" while still in the founding year,
 * otherwise "2020–2026" (en dash). Passing `currentYear` keeps the helper
 * testable without freezing the system clock.
 */
export function formatCopyrightYears(
  foundingYear: number = BIDMIO_FOUNDING_YEAR,
  currentYear: number = new Date().getFullYear(),
): string {
  if (currentYear <= foundingYear) {
    return String(foundingYear);
  }
  return `${foundingYear}–${currentYear}`;
}

/**
 * Full years since founding (2026 → 6). Never below 1, so copy like
 * "{{years}} rokov" stays grammatical if the clock is behind.
 */
export function yearsSinceFounding(
  foundingYear: number = BIDMIO_FOUNDING_YEAR,
  currentYear: number = new Date().getFullYear(),
): number {
  return Math.max(1, currentYear - foundingYear);
}

export function companyYearVars(
  currentYear: number = new Date().getFullYear(),
): CompanyYearVars {
  return {
    year: formatCopyrightYears(BIDMIO_FOUNDING_YEAR, currentYear),
    years: String(yearsSinceFounding(BIDMIO_FOUNDING_YEAR, currentYear)),
    currentYear: String(currentYear),
    previousYear: String(currentYear - 1),
    nextYear: String(currentYear + 1),
  };
}

/**
 * Fill {{currentYear}}, {{previousYear}}, {{nextYear}}, {{years}} and {{year}}
 * in any translation value.
 * Longer placeholders are replaced first so "{{year}}" cannot eat "{{years}}".
 */
export function interpolateCompanyYears<T>(
  value: T,
  vars: CompanyYearVars = companyYearVars(),
): T {
  if (typeof value === 'string') {
    return value
      .replaceAll('{{currentYear}}', vars.currentYear)
      .replaceAll('{{previousYear}}', vars.previousYear)
      .replaceAll('{{nextYear}}', vars.nextYear)
      .replaceAll('{{years}}', vars.years)
      .replaceAll('{{year}}', vars.year) as T;
  }
  if (Array.isArray(value)) {
    return value.map((item) => interpolateCompanyYears(item, vars)) as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      out[key] = interpolateCompanyYears(nested, vars);
    }
    return out as T;
  }
  return value;
}

/** Mutate locale JSON in place so returnObjects also get resolved years. */
export function applyCompanyYearPlaceholders(
  root: unknown,
  vars: CompanyYearVars = companyYearVars(),
): void {
  if (Array.isArray(root)) {
    for (let i = 0; i < root.length; i++) {
      if (typeof root[i] === 'string') {
        root[i] = interpolateCompanyYears(root[i], vars);
      } else {
        applyCompanyYearPlaceholders(root[i], vars);
      }
    }
    return;
  }
  if (root && typeof root === 'object') {
    const obj = root as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      const val = obj[key];
      if (typeof val === 'string') {
        obj[key] = interpolateCompanyYears(val, vars);
      } else {
        applyCompanyYearPlaceholders(val, vars);
      }
    }
  }
}
